import React from 'react';
import { FileText, CheckCircle, Clock, Target, ArrowRight } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { LeadForm } from '../components/LeadForm';

export const ProfileEvaluationPage = () => {
  const benefits = [
    'Understand your eligibility for different countries',
    'Get personalized pathway recommendations',
    'Know which visa options suit your profile',
    'Learn about potential challenges and solutions',
    'Receive a clear action plan for next steps'
  ];

  const evaluationCriteria = [
    { title: 'Education', description: 'Qualifications and relevance' },
    { title: 'Experience', description: 'Work history and skills' },
    { title: 'Language', description: 'English/German/Other proficiency' },
    { title: 'Goals', description: 'Short and long-term objectives' }
  ];

  return (
    <div data-testid="profile-evaluation-page" className="min-h-screen pt-20 pb-16 bg-[#FFFBF8]">
      {/* Header */}
      <section className="bg-gradient-to-br from-[#002A76] to-[#001F5C] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          <div className="max-w-3xl">
            <Badge className="bg-[#0AA06A] text-white mb-4 px-3 py-1">
              Free Assessment
            </Badge>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Get Your Free Profile Evaluation
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Discover which European countries and pathways are best suited for your unique profile. No spam, no pressure – just honest recommendations.
            </p>
            
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <Clock size={20} className="text-[#0AA06A]" />
                <span>Results within 48 hours</span>
              </div>
              <div className="flex items-center gap-2">
                <Target size={20} className="text-[#0AA06A]" />
                <span>Personalized recommendations</span>
              </div>
              <div className="flex items-center gap-2">
                <FileText size={20} className="text-[#0AA06A]" />
                <span>Detailed action plan</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left: Benefits */}
            <div className="space-y-12">
              {/* What You'll Get */}
              <div>
                <h2 className="text-2xl font-bold text-[#002A76] mb-6">What You'll Get</h2>
                <ul className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#0AA06A] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle size={14} className="text-white" />
                      </div>
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* What We Evaluate */}
              <div>
                <h2 className="text-2xl font-bold text-[#002A76] mb-6">What We Evaluate</h2>
                <div className="grid grid-cols-2 gap-4">
                  {evaluationCriteria.map((criteria, index) => (
                    <div key={index} className="bg-white rounded-xl p-4 border border-gray-100">
                      <h3 className="font-semibold text-[#002A76] mb-1">{criteria.title}</h3>
                      <p className="text-gray-600 text-sm">{criteria.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Trust Signals */}
              <div className="bg-[#F7F4F0] rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#002A76] flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">🔒</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#002A76] mb-1">Your Data is Safe</h3>
                    <p className="text-gray-600 text-sm">
                      We never share your information with third parties. Your profile is used solely to provide you with personalized recommendations.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div>
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 shadow-sm sticky top-24">
                <h2 className="text-xl font-bold text-[#002A76] mb-2">Start Your Evaluation</h2>
                <p className="text-gray-600 text-sm mb-6">
                  Fill in your details and our experts will evaluate your profile within 48 hours.
                </p>
                <LeadForm source="profile_evaluation" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-[#002A76] mb-4">
            Not Sure What to Expect?
          </h2>
          <p className="text-gray-600 mb-8">
            Join one of our free live sessions to learn more about immigration to Europe before filling the evaluation form.
          </p>
          <a href="/sessions">
            <button className="bg-[#002A76] hover:bg-[#001F5C] text-white rounded-full px-8 py-3 font-semibold inline-flex items-center">
              Explore Live Sessions
              <ArrowRight className="ml-2" size={18} />
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default ProfileEvaluationPage;
